import { FC } from 'react'
import PerSpacer from '../Spacer'
import PerText from '../Text'
import { SmartAvatar } from '../../../modules/avatar/SmartAvatar'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import type { EmployeeDisplayTinyInfo } from '../../../types'
import './perAvatarDashboardMonitoringTable.less'
import PerFlexRow from '../FlexRow'
import { useTranslation } from 'react-i18next'

export type PerAvatarTableCellProps = {
  employeeId: number
  firstName: string
  lastName: string
  nameDisplayPreference: NameDisplayPreference
  subtitle?: string
  whiteTextColor?: boolean
}

export const PerAvatarDashboardMonitoringTable: FC<PerAvatarTableCellProps> = ({
  employeeId,
  firstName,
  lastName,
  nameDisplayPreference,
  subtitle,
  whiteTextColor = false,
}) => {
  const fullName = computeFullname(nameDisplayPreference, firstName, lastName)

  return (
    <div className="per-avatar-table-cell" title={fullName}>
      <div className="per-avatar-table-cell__avatar">
        <SmartAvatar employeeId={employeeId} size="small" firstName={firstName} lastName={lastName} />
      </div>
      <PerSpacer ml="xxs" className="per-avatar-table-cell__names">
        <PerText fontWeight="bold" fontSize="xs" color={whiteTextColor ? 'white' : 'default'} className="truncate-text">
          {fullName}
        </PerText>
        {subtitle && (
          <PerText color={whiteTextColor ? 'white' : 'secondary'} fontSize="xs" className="truncate-text">
            {subtitle}
          </PerText>
        )}
      </PerSpacer>
    </div>
  )
}

export type PerAvatarDashboardBuManagerTableRowProps = {
  manager: Pick<EmployeeDisplayTinyInfo, 'id' | 'firstName' | 'lastName'>
  nameDisplayPreference: NameDisplayPreference
  showBuManagerLabel?: boolean
  variant?: 'white' | 'default'
}

export const PerAvatarDashboardBuManagerTooltipRow: FC<PerAvatarDashboardBuManagerTableRowProps> = ({
  manager,
  nameDisplayPreference,
  showBuManagerLabel = false,
  variant = 'default',
}) => {
  const [t] = useTranslation()
  const fullName = computeFullname(nameDisplayPreference, manager.firstName, manager.lastName)

  return (
    <PerFlexRow align="middle" noWrap mt="xxxs">
      <SmartAvatar employeeId={manager.id} size="small" firstName={manager.firstName} lastName={manager.lastName} />
      <PerFlexRow direction="column" ml="xxxs">
        {showBuManagerLabel && (
          <PerText color={variant} fontSize="xs" className="truncate-text">
            {t('dashboard.companyManager.filtersArea.distributions.buTab.buManagerRoleLabel')}
          </PerText>
        )}
        <PerText color={variant} fontWeight="bold" fontSize="xs" className="truncate-text">
          {fullName}
        </PerText>
      </PerFlexRow>
    </PerFlexRow>
  )
}

export type PerDashboardBuManagersTooltipContentProps = {
  buManagers: Array<Pick<EmployeeDisplayTinyInfo, 'id' | 'firstName' | 'lastName'>>
  nameDisplayPreference: NameDisplayPreference
}

export const PerDashboardBuManagersTooltipContent: FC<PerDashboardBuManagersTooltipContentProps> = ({
  buManagers,
  nameDisplayPreference,
}) => {
  const [t] = useTranslation()
  return (
    <>
      <PerText fontSize="xs">
        {t(
          `dashboard.companyManager.filtersArea.distributions.buTab.${
            buManagers.length > 1 ? 'buManagersRoleLabel' : 'buManagerRoleLabel'
          }`,
        )}
      </PerText>
      {buManagers.map((manager) => (
        <PerAvatarDashboardBuManagerTooltipRow
          key={String(manager.id)}
          manager={manager}
          nameDisplayPreference={nameDisplayPreference}
          variant="white"
        />
      ))}
    </>
  )
}
